/**
 * ModulationCyclingChart.tsx
 * 
 * Visual comparison of dirty system (frequent on/off cycling) vs 
 * clean system (full modulation) for Appendix A.
 * Shows how clean water enables efficient modulation.
 */

interface ModulationCyclingChartProps {
  width?: number;
  height?: number;
}

export function ModulationCyclingChart({ width = 600, height = 280 }: ModulationCyclingChartProps) {
  // Chart dimensions
  const margin = { top: 50, right: 40, bottom: 50, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const halfWidth = chartWidth / 2;

  // Time period for visualization
  const timeHours = 12;

  // Dirty system data - jagged, frequent on/off cycles
  const dirtyData = [
    { time: 0, output: 0 },
    { time: 0.3, output: 100 },
    { time: 0.8, output: 100 },
    { time: 0.9, output: 0 },
    { time: 1.5, output: 0 },
    { time: 1.8, output: 100 },
    { time: 2.3, output: 100 },
    { time: 2.4, output: 0 },
    { time: 3, output: 0 },
    { time: 3.3, output: 100 },
    { time: 3.9, output: 100 },
    { time: 4, output: 0 },
    { time: 4.7, output: 0 },
    { time: 5, output: 100 },
    { time: 5.6, output: 100 },
    { time: 5.7, output: 0 },
    { time: 6.4, output: 0 },
    { time: 6.7, output: 100 },
    { time: 7.3, output: 100 },
    { time: 7.4, output: 0 },
    { time: 8.1, output: 0 },
    { time: 8.4, output: 100 },
    { time: 9, output: 100 },
    { time: 9.1, output: 0 },
    { time: 9.8, output: 0 },
    { time: 10.1, output: 100 },
    { time: 10.6, output: 100 },
    { time: 10.7, output: 0 },
    { time: 11.5, output: 0 },
    { time: 12, output: 0 },
  ];

  // Clean system data - smooth, continuous modulation
  const cleanData = [
    { time: 0, output: 0 },
    { time: 0.5, output: 35 },
    { time: 1, output: 40 },
    { time: 2, output: 40 },
    { time: 3, output: 38 },
    { time: 4, output: 40 },
    { time: 5, output: 42 },
    { time: 6, output: 40 },
    { time: 7, output: 38 },
    { time: 8, output: 40 },
    { time: 9, output: 40 },
    { time: 10, output: 38 },
    { time: 11, output: 30 },
    { time: 11.5, output: 15 },
    { time: 12, output: 0 },
  ];

  // Scale functions for left side (dirty)
  const xScaleLeft = (t: number) => (t / timeHours) * halfWidth;
  const yScale = (v: number) => chartHeight - (v / 100) * chartHeight;

  // Scale functions for right side (clean) - offset by halfWidth
  const xScaleRight = (t: number) => halfWidth + (t / timeHours) * halfWidth;

  // Generate path
  const generatePath = (data: typeof dirtyData, scaleX: (t: number) => number) => {
    return data
      .map((point, i) => {
        const x = scaleX(point.time);
        const y = yScale(point.output);
        return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ');
  };

  const dirtyPath = generatePath(dirtyData, xScaleLeft);
  const cleanPath = generatePath(cleanData, xScaleRight);

  // Pipe icons dimensions
  const pipeY = chartHeight + 35;
  const pipeHeight = 20;
  const pipeWidth = halfWidth * 0.6;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="dp-modulation-cycling-chart"
      role="img"
      aria-label="Comparison of dirty system with frequent cycling versus clean system with full modulation"
    >
      <title>
        Modulation vs. Cycling: A dirty system cycles on and off frequently (left), 
        while a clean system runs smoothly with full modulation (right).
      </title>
      <defs>
        <style>{`
          .chart-axis { stroke: #64748b; stroke-width: 1.5; }
          .chart-grid { stroke: #e2e8f0; stroke-width: 1; stroke-dasharray: 4,4; }
          .chart-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; fill: #475569; }
          .chart-title { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; font-weight: 600; fill: #1e293b; }
          .section-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; font-weight: 600; fill: #1e293b; }
          .dirty-line { stroke: #dc2626; stroke-width: 2.5; fill: none; }
          .clean-line { stroke: #2563eb; stroke-width: 2.5; fill: none; }
          .pipe-dirty { fill: #7f1d1d; stroke: #dc2626; stroke-width: 2; }
          .pipe-clean { fill: #3b82f6; stroke: #1e40af; stroke-width: 2; opacity: 0.8; }
          .sludge-pattern { fill: #991b1b; }
          .water-pattern { fill: #60a5fa; opacity: 0.3; }
        `}</style>
        
        {/* Pattern for sludge in dirty pipe */}
        <pattern id="sludge" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.5" className="sludge-pattern" />
          <circle cx="6" cy="6" r="1.5" className="sludge-pattern" />
        </pattern>
        
        {/* Pattern for clean water */}
        <pattern id="water" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="10" y2="10" stroke="#60a5fa" strokeWidth="1" opacity="0.3" />
        </pattern>
      </defs>

      {/* Chart group */}
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        
        {/* Section divider */}
        <line
          x1={halfWidth}
          y1={-30}
          x2={halfWidth}
          y2={chartHeight + 80}
          stroke="#cbd5e1"
          strokeWidth="2"
          strokeDasharray="5,5"
        />

        {/* Section labels */}
        <text x={halfWidth / 2} y={-35} textAnchor="middle" className="section-label">
          Dirty System
        </text>
        <text x={halfWidth + halfWidth / 2} y={-35} textAnchor="middle" className="section-label">
          Clean System
        </text>

        {/* Grid lines - left side */}
        {[0, 50, 100].map((v) => (
          <line
            key={`grid-left-${v}`}
            x1={0}
            y1={yScale(v)}
            x2={halfWidth - 10}
            y2={yScale(v)}
            className="chart-grid"
          />
        ))}

        {/* Grid lines - right side */}
        {[0, 50, 100].map((v) => (
          <line
            key={`grid-right-${v}`}
            x1={halfWidth + 10}
            y1={yScale(v)}
            x2={chartWidth}
            y2={yScale(v)}
            className="chart-grid"
          />
        ))}

        {/* Left Y-axis */}
        <line x1={0} y1={0} x2={0} y2={chartHeight} className="chart-axis" />
        <text x={-10} y={yScale(100)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          On
        </text>
        <text x={-10} y={yScale(0)} textAnchor="end" alignmentBaseline="middle" className="chart-label">
          Off
        </text>

        {/* Right Y-axis */}
        <line x1={chartWidth} y1={0} x2={chartWidth} y2={chartHeight} className="chart-axis" />
        <text x={chartWidth + 10} y={yScale(50)} textAnchor="start" alignmentBaseline="middle" className="chart-label">
          Modulating
        </text>

        {/* Left X-axis */}
        <line x1={0} y1={chartHeight} x2={halfWidth - 10} y2={chartHeight} className="chart-axis" />
        <text x={0} y={chartHeight + 15} textAnchor="start" className="chart-label" fontSize="10">
          0h
        </text>
        <text x={halfWidth / 2} y={chartHeight + 15} textAnchor="middle" className="chart-label" fontSize="10">
          6h
        </text>
        <text x={halfWidth - 10} y={chartHeight + 15} textAnchor="end" className="chart-label" fontSize="10">
          12h
        </text>

        {/* Right X-axis */}
        <line x1={halfWidth + 10} y1={chartHeight} x2={chartWidth} y2={chartHeight} className="chart-axis" />
        <text x={halfWidth + 10} y={chartHeight + 15} textAnchor="start" className="chart-label" fontSize="10">
          0h
        </text>
        <text x={halfWidth + halfWidth / 2} y={chartHeight + 15} textAnchor="middle" className="chart-label" fontSize="10">
          6h
        </text>
        <text x={chartWidth} y={chartHeight + 15} textAnchor="end" className="chart-label" fontSize="10">
          12h
        </text>

        {/* Dirty system line */}
        <path d={dirtyPath} className="dirty-line" />

        {/* Clean system line */}
        <path d={cleanPath} className="clean-line" />

        {/* Pipe icons */}
        
        {/* Dirty pipe - left side */}
        <rect
          x={(halfWidth - pipeWidth) / 2}
          y={pipeY}
          width={pipeWidth}
          height={pipeHeight}
          rx="3"
          className="pipe-dirty"
        />
        <rect
          x={(halfWidth - pipeWidth) / 2 + 2}
          y={pipeY + 2}
          width={pipeWidth - 4}
          height={pipeHeight - 4}
          rx="2"
          fill="url(#sludge)"
        />
        <text
          x={halfWidth / 2}
          y={pipeY + pipeHeight + 15}
          textAnchor="middle"
          className="chart-label"
          fontSize="11"
          fill="#991b1b"
        >
          Sludge-filled pipe
        </text>

        {/* Clean pipe - right side */}
        <rect
          x={halfWidth + (halfWidth - pipeWidth) / 2}
          y={pipeY}
          width={pipeWidth}
          height={pipeHeight}
          rx="3"
          className="pipe-clean"
        />
        <rect
          x={halfWidth + (halfWidth - pipeWidth) / 2 + 2}
          y={pipeY + 2}
          width={pipeWidth - 4}
          height={pipeHeight - 4}
          rx="2"
          fill="url(#water)"
        />
        <text
          x={halfWidth + halfWidth / 2}
          y={pipeY + pipeHeight + 15}
          textAnchor="middle"
          className="chart-label"
          fontSize="11"
          fill="#1e40af"
        >
          Clear blue pipe
        </text>

        {/* Annotations */}
        <text x={halfWidth / 2} y={-15} textAnchor="middle" className="chart-label" fontSize="11" fill="#dc2626">
          Frequent on/off cycles
        </text>
        <text x={halfWidth + halfWidth / 2} y={-15} textAnchor="middle" className="chart-label" fontSize="11" fill="#2563eb">
          Smooth, continuous operation
        </text>

      </g>
    </svg>
  );
}
